<template>
  <div
    :class="[style.variant, mobileVersion ? style.mobile : '']"
    :style="{padding: withImg ? '5px 5px' : ''}"
  >
    <div v-if="withImg" :class="style.img">
      <input
        :id="`${index}-${selectedQuestion}-variant`"
        type="file"
        accept="image/*"
        @change="setImg($event, `${index}-${selectedQuestion}-variant`)"
      />
      <label
        :for="`${index}-${selectedQuestion}-variant`"
        :style="{backgroundImage: getImg(variant)}"
      />
    </div>

    <input
      type="text"
      :value="variant?.text"
      @input="setTextFunction($event as InputEvent, index)"
    />

    <span @click="deleteFunction(index)">
      <div :class="style.deleteIcon" />
    </span>
  </div>
</template>

<script lang="ts">
/* eslint-disable no-unused-vars */
import {defineComponent, PropType} from "vue";
import style from "./InputButtonElementQuestion.module.scss";
import {DOMAIN} from "@/http";
import ButtonCross from "@/elements/buttonCross/buttonCross.vue";
import {Quiz} from "@/page/quiz/typeQuiz";

type CachedPreview = {
  file: File;
  url: string;
};

type VariantImagePreviewCache = Record<string, string>;

interface Window {
  __readdsVariantImagePreviewUrls?: VariantImagePreviewCache;
}

function getVariantImagePreviewCache(): VariantImagePreviewCache {
  const win = window as Window;

  if (typeof win === "undefined") {
    return {};
  }

  if (!win.__readdsVariantImagePreviewUrls) {
    win.__readdsVariantImagePreviewUrls = {};
  }

  return win.__readdsVariantImagePreviewUrls;
}

export default defineComponent({
  name: "InputButtonElementQuestion",
  components: {ButtonCross},
  props: {
    deleteFunction: {
      type: Function as PropType<(index: number) => void>,
      required: true
    },
    setTextFunction: {
      type: Function as PropType<(event: InputEvent, index: number) => void>,
      required: true
    },
    variant: {
      type: Object as PropType<{text: string; img?: string}>,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    withImg: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      style,
      DOMAIN,
      srcFileChanged: "",
      srcFileChangedName: "",
      cachedPreviewUrls: {} as Record<string, CachedPreview>
    };
  },

  beforeUnmount() {
    this.srcFileChanged = "";
    this.srcFileChangedName = "";
    this.cachedPreviewUrls = {};
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },
    selectedQuestion() {
      return this.$quizStore.getSelectedQuestion;
    },
    selectedVersion() {
      return this.$quizStore.getSelectedVersion;
    },
    mobileVersion() {
      return this.selectedVersion === "Мобильная";
    },
    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles || [];
    }
  },

  methods: {
    getFileExtension(file: File): string {
      const fileNameParts = file.name.split(".");
      const extensionFromName =
        fileNameParts.length > 1 ? fileNameParts.pop() : "";
      const extensionFromType = file.type.split("/").pop() || "jpg";

      return extensionFromName || extensionFromType;
    },

    getFileNameFromPath(path: string): string {
      return path.split("/").pop() || path;
    },

    getSharedVariantPreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const previewCache = getVariantImagePreviewCache();

      return previewCache[fileName] || previewCache[cachedFileName] || "";
    },

    setSharedVariantPreview(fileName: string, previewUrl: string): void {
      if (!fileName || !previewUrl) {
        return;
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const previewCache = getVariantImagePreviewCache();
      const oldPreviewUrl = previewCache[cachedFileName];

      if (
        oldPreviewUrl &&
        oldPreviewUrl !== previewUrl &&
        oldPreviewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(oldPreviewUrl);
      }

      previewCache[fileName] = previewUrl;
      previewCache[cachedFileName] = previewUrl;
    },

    upsertCachedFile(file: File): void {
      for (let index = this.cachedFiles.length - 1; index >= 0; index -= 1) {
        if (this.cachedFiles[index].name === file.name) {
          this.cachedFiles.splice(index, 1);
        }
      }

      this.cachedFiles.push(file);
    },

    getCachedFilePreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const sharedPreview = this.getSharedVariantPreview(cachedFileName);

      if (sharedPreview) {
        return sharedPreview;
      }

      const cachedFile = this.cachedFiles.find((file: File) => {
        return file.name === fileName || file.name === cachedFileName;
      });

      if (!cachedFile) {
        return "";
      }

      const currentPreview =
        this.cachedPreviewUrls[fileName] ||
        this.cachedPreviewUrls[cachedFileName];

      if (currentPreview?.file === cachedFile) {
        this.setSharedVariantPreview(cachedFileName, currentPreview.url);

        return currentPreview.url;
      }

      const previewUrl = URL.createObjectURL(cachedFile);
      const preview = {
        file: cachedFile,
        url: previewUrl
      };

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: preview,
        [cachedFileName]: preview
      };

      this.setSharedVariantPreview(cachedFileName, previewUrl);

      return previewUrl;
    },

    revokeCachedFilePreview(fileName: string): void {
      if (!fileName) {
        return;
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const updatedPreviewUrls = {...this.cachedPreviewUrls};

      delete updatedPreviewUrls[fileName];
      delete updatedPreviewUrls[cachedFileName];

      this.cachedPreviewUrls = updatedPreviewUrls;
    },

    revokeAllCachedFilePreviews(): void {
      this.cachedPreviewUrls = {};
    },

    async setImg(e: Event, name: string) {
      const target = e.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) return;

      const file = target.files[0];
      const extension = this.getFileExtension(file);
      const newFileName = `${name}.${extension}`;
      const newFile = new File([file], newFileName, {
        type: file.type,
        lastModified: file.lastModified
      });

      const srcFile = URL.createObjectURL(newFile);
      const label = target.parentElement?.querySelector("label") as HTMLElement;
      if (label) {
        label.style.backgroundImage = `url(${srcFile})`;
      }

      this.srcFileChanged = srcFile;
      this.srcFileChangedName = newFile.name;

      try {
        this.revokeCachedFilePreview(newFile.name);
        this.upsertCachedFile(newFile);

        this.cachedPreviewUrls = {
          ...this.cachedPreviewUrls,
          [newFile.name]: {
            file: newFile,
            url: srcFile
          }
        };
        this.setSharedVariantPreview(newFile.name, srcFile);

        this.variant.img = newFile.name;
      } catch (err) {
        console.error("Ошибка при загрузке изображения:", err);
      }
    },

    getImg(variant: {img?: string}) {
      const path = variant?.img;

      if (!path) {
        return `url('https://${this.DOMAIN}/api/img?name=logoWidget.svg')`;
      }

      if (path.startsWith("blob:") || path.startsWith("data:")) {
        return `url('${path}')`;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return `url('${path}')`;
      }

      const cachedPreview = this.getCachedFilePreview(path);

      if (cachedPreview) {
        return `url('${cachedPreview}')`;
      }

      if (path === this.srcFileChangedName && this.srcFileChanged) {
        return `url('${this.srcFileChanged}')`;
      }

      return `url('https://${this.DOMAIN}/api/img?name=${encodeURIComponent(path)}')`;
    }
  }
});
</script>
